// ===========================================
// Convert to Student Dialog
// ===========================================
// Admin-initiated Meet & Greet conversion: creates the family, parent user
// and student accounts, and records the registration-fee outcome the admin
// chose (already paid online / paid another way / not collected yet / waived)
// in the same transaction. Extracted from MeetAndGreetPage so that page does
// not grow. md/plan/family-payment-history.md Task 4.4.

import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Checkbox,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useAdminConvertMeetAndGreet, useMeetAndGreetRegistrationFee } from '../../hooks/useMeetAndGreet';
import { usePermissions } from '../../hooks/usePermissions';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import { schoolTodayUtcMidnight, toUTCDateKey } from '../../utils/dateUtils';
import { formatPaymentDay } from '../../utils/invoiceDisplay';
import { formatCurrencyFromDollars } from '../../utils/currency';
import { apiErrorCode, apiErrorMessage } from '../../utils/apiError';
import type { MeetAndGreet } from '../../api/meetAndGreet.api';
import type { AdminConvertRegistrationFee } from '../../api/familyPayments.api';
import RegistrationFeeFields, {
  RegistrationFeeFormValues,
  isRegistrationFeeValid,
} from './families/RegistrationFeeFields';

type ConvertMode = 'PAID_OTHER' | 'NOT_COLLECTED' | 'WAIVED';

interface ConvertToStudentDialogProps {
  open: boolean;
  booking: MeetAndGreet | null;
  onClose: () => void;
  /** Called after a successful conversion — the caller closes the dialog and clears selection. */
  onConverted: () => void;
}

export default function ConvertToStudentDialog({ open, booking, onClose, onConverted }: ConvertToStudentDialogProps) {
  const { can } = usePermissions();
  const canWriteInvoices = can('operations_invoices', 'write');
  const timeZone = useSchoolTimezone();
  const todayKey = toUTCDateKey(schoolTodayUtcMidnight(timeZone));

  const feeQuery = useMeetAndGreetRegistrationFee(open ? booking?.id ?? null : null);
  const convertMutation = useAdminConvertMeetAndGreet();

  const [mode, setMode] = useState<ConvertMode | ''>('');
  const [feeValues, setFeeValues] = useState<RegistrationFeeFormValues>({
    amount: '',
    method: 'BANK_TRANSFER',
    paidAt: todayKey,
    reference: '',
    notes: '',
  });
  const [waiveReason, setWaiveReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  // #430 D14: the office's logged "this is a different family" tick.
  const [confirmNewFamily, setConfirmNewFamily] = useState(false);
  // The server can say POSSIBLE_EXISTING_FAMILY for a match the row did not carry yet.
  const [serverPossibleMatch, setServerPossibleMatch] = useState(false);

  // Reset the forcing function every time the dialog opens for a (possibly
  // different) booking — no mode is pre-selected.
  useEffect(() => {
    if (!open) return;
    setMode('');
    setWaiveReason('');
    setSubmitError(null);
    setConfirmNewFamily(false);
    setServerPossibleMatch(false);
    setFeeValues({ amount: '', method: 'BANK_TRANSFER', paidAt: todayKey, reference: '', notes: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, booking?.id]);

  // Prefill the amount once the school's default registration fee resolves.
  useEffect(() => {
    const defaultAmount = feeQuery.data?.defaultAmount;
    if (open && defaultAmount) {
      setFeeValues((prev) => (prev.amount === '' ? { ...prev, amount: defaultAmount } : prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, feeQuery.data?.defaultAmount]);

  const handleClose = () => {
    if (convertMutation.isPending) return;
    onClose();
  };

  const onlinePayment = feeQuery.data?.onlinePayment ?? null;

  // A 409 leaves `submitError` set ("already paid online"); the mutation's
  // onError also refetches the fee query, which then resolves to an online
  // payment. Without this, the stale error text sits under the "Paid online"
  // success Alert until the admin closes the dialog.
  useEffect(() => {
    if (onlinePayment) {
      setSubmitError(null);
    }
  }, [onlinePayment]);

  const registrationFee: AdminConvertRegistrationFee | null = onlinePayment
    ? { mode: 'PAID_ONLINE' }
    : mode === 'PAID_OTHER'
      ? isRegistrationFeeValid(feeValues, todayKey)
        ? {
            mode: 'PAID_OTHER',
            amount: parseFloat(feeValues.amount),
            method: feeValues.method,
            paidAt: feeValues.paidAt,
            reference: feeValues.reference || undefined,
            notes: feeValues.notes || undefined,
          }
        : null
      : mode === 'NOT_COLLECTED'
        ? { mode: 'NOT_COLLECTED' }
        : mode === 'WAIVED'
          ? waiveReason.trim()
            ? { mode: 'WAIVED', reason: waiveReason.trim() }
            : null
          : null;

  const existingFamily = booking?.existingFamily ?? null;
  const hardCollision = existingFamily?.hardCollision === true;
  const needsDifferentFamilyTick = !hardCollision && (existingFamily !== null || serverPossibleMatch);

  const canSubmit =
    !!booking &&
    !hardCollision &&
    (!needsDifferentFamilyTick || confirmNewFamily) &&
    !feeQuery.isLoading &&
    !feeQuery.isFetching &&
    !feeQuery.isError &&
    registrationFee !== null &&
    !convertMutation.isPending;

  const handleSubmit = async () => {
    if (!booking || !registrationFee) return;
    setSubmitError(null);
    try {
      await convertMutation.mutateAsync({
        id: booking.id,
        registrationFee,
        ...(needsDifferentFamilyTick && confirmNewFamily ? { confirmNewFamily: true } : {}),
      });
      onConverted();
    } catch (error) {
      // The mutation's onError already shows a snackbar; keep the dialog open
      // and readable so the admin can correct their choice.
      if (apiErrorCode(error) === 'POSSIBLE_EXISTING_FAMILY') setServerPossibleMatch(true);
      setSubmitError(apiErrorMessage(error, 'Failed to convert to student'));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Convert to Student</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Creates the family and student accounts now. Record how the registration fee was handled.
        </Alert>

        {hardCollision && (
          <Alert severity="error" sx={{ mb: 2 }}>
            This email already has a login with the school, so a second account can't be created. Add the child on
            the Students page (Add Student, then choose their family).
          </Alert>
        )}
        {needsDifferentFamilyTick && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              This booking shares an email with a family that already has an account. Normally the child is added on
              the Students page (Add Student, then choose their family). Convert only if this is a different family.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmNewFamily}
                  onChange={(e) => setConfirmNewFamily(e.target.checked)}
                  disabled={convertMutation.isPending}
                />
              }
              label="I've checked: this is a different family"
            />
          </Alert>
        )}

        {booking && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>
              Accounts to be created:
            </Typography>
            <Typography variant="body2">
              <strong>Student:</strong> {booking.studentFirstName} {booking.studentLastName}
            </Typography>
            <Typography variant="body2">
              <strong>Parent:</strong> {booking.contact1Name} ({booking.contact1Email})
            </Typography>
            <Typography variant="body2">
              <strong>Family:</strong>{' '}
              {booking.contact1Name?.split(' ').slice(1).join(' ') || booking.contact1Name} Family
            </Typography>
          </Box>
        )}

        <Typography variant="body2" sx={{ mb: 2 }}>
          We'll email the parent a link to set their password. This action cannot be undone.
        </Typography>

        <Typography variant="subtitle2" gutterBottom>
          Registration fee
        </Typography>

        {feeQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : feeQuery.isError ? (
          <Alert severity="error">
            Couldn't check whether this family already paid online. Try again before converting.
          </Alert>
        ) : onlinePayment ? (
          <Alert severity="success">
            Paid online
            {onlinePayment.paidAt ? ` ${formatPaymentDay(onlinePayment.paidAt, timeZone)}` : ''} ·{' '}
            {formatCurrencyFromDollars(onlinePayment.amount)}
          </Alert>
        ) : (
          <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as ConvertMode)}>
            <FormControlLabel
              value="PAID_OTHER"
              control={<Radio />}
              disabled={!canWriteInvoices || convertMutation.isPending}
              label="Paid another way"
            />
            {mode === 'PAID_OTHER' && (
              <Box sx={{ pl: 4, pb: 2 }}>
                <RegistrationFeeFields
                  values={feeValues}
                  onChange={setFeeValues}
                  todayKey={todayKey}
                  disabled={convertMutation.isPending}
                />
              </Box>
            )}

            <FormControlLabel
              value="NOT_COLLECTED"
              control={<Radio />}
              disabled={convertMutation.isPending}
              label="Not collected yet (record later)"
            />
            {mode === 'NOT_COLLECTED' && (
              <Typography variant="caption" color="text.secondary" sx={{ pl: 4, pb: 1, display: 'block' }}>
                You can record it later from the family's details.
              </Typography>
            )}

            <FormControlLabel
              value="WAIVED"
              control={<Radio />}
              disabled={!canWriteInvoices || convertMutation.isPending}
              label="Waive the fee"
            />
            {mode === 'WAIVED' && (
              <Box sx={{ pl: 4, pb: 2 }}>
                <TextField
                  fullWidth
                  required
                  label="Reason"
                  value={waiveReason}
                  onChange={(e) => setWaiveReason(e.target.value)}
                  multiline
                  rows={2}
                  disabled={convertMutation.isPending}
                  inputProps={{ maxLength: 500 }}
                />
              </Box>
            )}

            {!canWriteInvoices && (
              <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
                Recording or waiving the registration fee needs the Invoices (write) permission.
              </Typography>
            )}
          </RadioGroup>
        )}

        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={convertMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={handleSubmit}
          sx={{
            bgcolor: '#96DAC9',
            color: '#080808',
            '&:hover': { bgcolor: '#7bc9b5' },
            '&:disabled': { bgcolor: 'action.disabledBackground' },
          }}
        >
          {convertMutation.isPending ? 'Creating accounts...' : 'Convert to Student'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
